import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { InputSwitch } from 'primereact/inputswitch';
import FinancialAccountService from '../../services/FinancialAccountService';
import SupplierService from '../../services/SupplierService';

export default function FinancialAccountEditPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useRef(null);
  const [loading, setLoading] = useState(false);
  const [account, setAccount] = useState({
    name: '',
    accountType: 1, // Default to Cash
    accountNumber: '',
    openingBalance: 0,
    currencyCode: 'TRY',
    isActive: true,
    associatedBankAccountId: null,
    merchantNo: '',
    terminalNo: '',
    defaultCommissionRate: 0,
    defaultValueDays: 0,
    cardLimit: 0,
    statementDay: 1,
    paymentDueDay: 10,
    supplierId: null
  });

  const [bankAccounts, setBankAccounts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const accountTypes = [
    { label: 'Kasa Hesabı', value: 1 },
    { label: 'Banka Hesabı', value: 2 },
    { label: 'Firma Kredi Kartı', value: 3 },
    { label: 'POS Hesabı', value: 4 },
    { label: 'Sanal POS', value: 5 }
  ];

  useEffect(() => {
    loadBankAccounts();
    loadSuppliers();
    if (id) {
      loadAccount();
    }
  }, [id]);

  const loadSuppliers = async () => {
    try {
      const data = await SupplierService.getAllSuppliers();
      setSuppliers(data || []);
    } catch (err) {
      console.error("Tedarikçiler yüklenemedi", err);
    }
  };

  const loadBankAccounts = async () => {
    try {
      const res = await FinancialAccountService.getAll();
      if (res.success) {
        setBankAccounts((res.data || []).filter(a => a.accountType === 2));
      }
    } catch (err) {
      console.error("Banka hesapları yüklenemedi", err);
    }
  };

  const loadAccount = async () => {
    try {
      setLoading(true);
      const res = await FinancialAccountService.getDetail(id);
      if (res.success) {
        setAccount(res.data.account);
      } else {
        toast.current.show({ severity: 'error', summary: 'Hata', detail: res.message });
      }
    } catch (err) {
      toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Hesap bilgileri yüklenemedi' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!account.name) {
      toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Hesap adı zorunludur' });
      return;
    }

    try {
      setLoading(true);
      let res;
      if (id) {
        res = await FinancialAccountService.update(account);
      } else {
        res = await FinancialAccountService.create(account);
      }

      if (res.success) {
        toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Hesap başarıyla kaydedildi' });
        setTimeout(() => navigate('/finance/accounts'), 1500);
      } else {
        toast.current.show({ severity: 'error', summary: 'Hata', detail: res.message });
      }
    } catch (err) {
      toast.current.show({ severity: 'error', summary: 'Hata', detail: 'İşlem sırasında bir hata oluştu' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <Toast ref={toast}  appendTo={mounted ? document.body : "self"} />

      <div className="flex items-center gap-4 mb-6">
        <Button
          icon="pi pi-arrow-left"
          className="p-button-text p-button-secondary p-button-rounded"
          onClick={() => navigate(-1)}
        />
        <div>
          <h1 className="text-2xl font-black text-slate-800 m-0">
            {id ? 'Hesabı Düzenle' : 'Yeni Hesap Tanımla'}
          </h1>
          <p className="text-slate-500 font-medium">Sistemdeki finansal hesaplarınızı tanımlayın</p>
        </div>
      </div>

      <Card className="shadow-xl border border-slate-200 rounded-3xl overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-2">
          <div className="flex flex-col gap-2">
            <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1">HESAP ADI</label>
            <InputText
              value={account.name}
              onChange={(e) => setAccount({ ...account, name: e.target.value })}
              placeholder="Örn: Merkez Kasa, Ziraat Bankası vb."
              className="rounded-xl border-slate-200 py-3 font-bold"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1">HESAP TİPİ</label>
            <Dropdown
              value={account.accountType}
              options={accountTypes}
              onChange={(e) => setAccount({ ...account, accountType: e.value })}
              className="rounded-xl border-slate-200 font-bold h-[50px] flex items-center"
              disabled={!!id} // Type cannot be changed after creation for consistency
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1">HESAP / KART NO</label>
            <InputText
              value={account.accountNumber}
              onChange={(e) => setAccount({ ...account, accountNumber: e.target.value })}
              placeholder="Hesap veya IBAN no"
              className="rounded-xl border-slate-200 py-3 font-bold"
            />
          </div>

          {!id && (
            <div className="flex flex-col gap-2">
              <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1">AÇILIŞ BAKİYESİ</label>
              <InputNumber
                value={account.openingBalance}
                onValueChange={(e) => setAccount({ ...account, openingBalance: e.value })}
                mode="currency"
                currency="TRY"
                locale="tr-TR"
                className="w-full"
                inputClassName="rounded-xl border-slate-200 py-3 font-bold"
              />
            </div>
          )}

          <div className="flex flex-col gap-2">
            <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1">PARA BİRİMİ</label>
            <InputText
              value={account.currencyCode}
              onChange={(e) => setAccount({ ...account, currencyCode: e.target.value })}
              placeholder="TRY"
              className="rounded-xl border-slate-200 py-3 font-bold"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1">DURUM</label>
            <div className="flex items-center gap-3 h-[50px]">
              <InputSwitch
                checked={account.isActive}
                onChange={(e) => setAccount({ ...account, isActive: e.value })}
              />
              <span className="font-bold text-slate-700">{account.isActive ? 'Aktif' : 'Pasif'}</span>
            </div>
          </div>

          {account.accountType === 4 && (
            <>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1">AKTARIŞ BANKA HESABI</label>
                <Dropdown
                  value={account.associatedBankAccountId}
                  options={bankAccounts}
                  optionLabel="name"
                  optionValue="id"
                  onChange={(e) => setAccount({ ...account, associatedBankAccountId: e.value })}
                  placeholder="Seçiniz..."
                  className="rounded-xl border-slate-200 font-bold h-[50px] flex items-center"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1">ÜYE İŞYERİ NO</label>
                <InputText
                  value={account.merchantNo || ''}
                  onChange={(e) => setAccount({ ...account, merchantNo: e.target.value })}
                  placeholder="POS Üye İşyeri No"
                  className="rounded-xl border-slate-200 py-3 font-bold"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1">TERMİNAL NO</label>
                <InputText
                  value={account.terminalNo || ''}
                  onChange={(e) => setAccount({ ...account, terminalNo: e.target.value })}
                  placeholder="POS Terminal No"
                  className="rounded-xl border-slate-200 py-3 font-bold"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1">KOMİSYON ORANI (%)</label>
                <InputNumber
                  value={account.defaultCommissionRate}
                  onValueChange={(e) => setAccount({ ...account, defaultCommissionRate: e.value })}
                  min={0}
                  max={100}
                  minFractionDigits={2}
                  maxFractionDigits={4}
                  placeholder="0.00"
                  inputClassName="rounded-xl border-slate-200 py-3 font-bold w-full animate-fade-in"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1">VALÖR GÜNÜ</label>
                <InputNumber
                  value={account.defaultValueDays}
                  onValueChange={(e) => setAccount({ ...account, defaultValueDays: e.value })}
                  min={0}
                  placeholder="Gün sayısı"
                  inputClassName="rounded-xl border-slate-200 py-3 font-bold w-full animate-fade-in"
                />
              </div>
            </>
          )}

          {account.accountType === 5 && (
            <div className="flex flex-col gap-2">
              <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1">TEDARİKÇİ</label>
              <Dropdown
                value={account.supplierId || null}
                options={suppliers}
                optionLabel="name"
                optionValue="id"
                onChange={(e) => setAccount({ ...account, supplierId: e.value })}
                placeholder="Tedarikçi Seçiniz..."
                className="rounded-xl border-slate-200 font-bold h-[50px] flex items-center"
                filter
                appendTo={mounted ? document.body : "self"}
              />
            </div>
          )}

          {account.accountType === 3 && (
            <>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1">KART LİMİTİ</label>
                <InputNumber
                  value={account.cardLimit}
                  onValueChange={(e) => setAccount({ ...account, cardLimit: e.value })}
                  mode="currency"
                  currency={account.currencyCode || 'TRY'}
                  locale="tr-TR"
                  inputClassName="rounded-xl border-slate-200 py-3 font-bold w-full animate-fade-in"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1">HESAP KESİM GÜNÜ</label>
                <InputNumber
                  value={account.statementDay}
                  onValueChange={(e) => setAccount({ ...account, statementDay: e.value })}
                  min={1}
                  max={31}
                  placeholder="Ayın günü (1-31)"
                  inputClassName="rounded-xl border-slate-200 py-3 font-bold w-full animate-fade-in"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1">SON ÖDEME GÜNÜ</label>
                <InputNumber
                  value={account.paymentDueDay}
                  onValueChange={(e) => setAccount({ ...account, paymentDueDay: e.value })}
                  min={1}
                  max={31}
                  placeholder="Ayın günü (1-31)"
                  inputClassName="rounded-xl border-slate-200 py-3 font-bold w-full animate-fade-in"
                />
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-slate-100">
          <Button
            label="Vazgeç"
            icon="pi pi-times"
            className="p-button-text p-button-secondary rounded-xl font-bold"
            onClick={() => navigate('/finance/accounts')}
          />
          <Button
            label="Kaydet"
            icon="pi pi-check"
            className="!bg-emerald-600 border-none rounded-xl font-black px-8 py-3 shadow-lg shadow-emerald-600/20"
            onClick={handleSave}
            loading={loading}
          />
        </div>
      </Card>
    </div>
  );
}
